import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-hot-toast'

import {
  AppButton,
  PasswordCriteria,
  StickyHeaderNavigation,
} from '@/components'
import { BaseView, BaseText } from '@/components/base'
import { TextInputForm } from '@/components/form'
import { inputLimits } from '@/constants/inputLimits'
import { useUpdatePassword } from '@/domain'
import { usePasswordCriteriaControl } from '@/hooks'
import { ICreateNewPasswordSchema, useCreateNewPasswordSchema } from '@/schemas'
import useOffsets from '@/hooks/useOffsets'

export const CreateNewPasswordScreen: React.FC = () => {
  const { t } = useTranslation()
  const offsets = useOffsets()
  const navigate = useNavigate()
  const [isValidForm, setIsValidForm] = useState(true)

  const {
    allCriteriaMet,
    setAllCriteriaMet,
    checkCriteriaOnBlur,
    showErrorState,
  } = usePasswordCriteriaControl()

  const createNewPasswordSchema = useCreateNewPasswordSchema()

  const {
    handleSubmit,
    control,
    formState,
    watch,
    setFocus,
    trigger,
    setValue,
  } = useForm<ICreateNewPasswordSchema>({
    resolver: zodResolver(createNewPasswordSchema),
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmNewPassword: '',
    },
    mode: 'onChange',
  })

  const newPassword = watch('newPassword')
  const confirmNewPassword = watch('confirmNewPassword')

  const { mutateAsync: updatePassword, isPending } = useUpdatePassword({
    onSuccess: () => {
      toast.success(t('form:success.passwordUpdated'))
      navigate('/security', { replace: true })
    },
    onError: ({ errorMessage }) => {
      toast.error(errorMessage || t('api:errors.somethingWrong'))
    },
  })

  const isFormReadyToSubmit = formState.isDirty && isValidForm && allCriteriaMet

  const handleSubmitForm = async (formData: ICreateNewPasswordSchema) => {
    await updatePassword({
      oldPassword: formData.currentPassword,
      newPassword: formData.newPassword,
    })
  }

  const handleBlurNewPassword = () => {
    // Mirror RN: when leaving password, validate criteria and match if confirm exists
    if (confirmNewPassword) {
      trigger('confirmNewPassword')
    }
    checkCriteriaOnBlur()
  }

  useEffect(() => {
    setIsValidForm(formState.isValid)
  }, [formState.isValid])

  return (
    <>
      <BaseView style={{ height: '100vh' }} className="h-full flex flex-col">
        <StickyHeaderNavigation />
        <BaseView
          style={{
            paddingTop: offsets.top + 60,
          }}
          className="flex-1"
          px={13}
          gap={28}
        >
          <BaseView gap={10}>
            <BaseText
              fontSize={32}
              fontFamily="semibold"
              translationKey="screens:security.createNewPassword.title"
            />

            <BaseView gap={20}>
              <BaseView gap={12}>
                <TextInputForm
                  control={control}
                  name="currentPassword"
                  label={t('form:labels.enterCurrentPassword')}
                  secureTextEntry
                  textContentType="currentPassword"
                  maxLength={inputLimits.password.max}
                  onSubmitEditing={() => setFocus('newPassword')}
                />

                <TextInputForm
                  control={control}
                  name="newPassword"
                  label={t('form:labels.enterNewPassword')}
                  secureTextEntry
                  textContentType="newPassword"
                  maxLength={inputLimits.password.max}
                  onBlur={handleBlurNewPassword}
                  onChange={(e) => {
                    const value = e.target.value
                    setValue('newPassword', value)

                    // If confirm has value, re-validate match to show/hide mismatch promptly
                    if (
                      value &&
                      confirmNewPassword &&
                      confirmNewPassword.length >= 8
                    ) {
                      trigger('confirmNewPassword')
                    }
                  }}
                  onSubmitEditing={() => setFocus('confirmNewPassword')}
                />

                <TextInputForm
                  control={control}
                  name="confirmNewPassword"
                  label={t('form:labels.confirmNewPassword')}
                  secureTextEntry
                  textContentType="newPassword"
                  maxLength={inputLimits.password.max}
                  onBlur={() => {
                    // Validate on blur to surface mismatch warning like RN
                    if (confirmNewPassword.length > 1) {
                      trigger('confirmNewPassword')
                    }
                  }}
                />
              </BaseView>

              <PasswordCriteria
                password={newPassword || ''}
                setAllCriteriaMet={setAllCriteriaMet}
                showErrorState={showErrorState}
              />
            </BaseView>
          </BaseView>
        </BaseView>

        <BaseView mt="auto" px={13} style={{ paddingBottom: offsets.bottom }}>
          <AppButton
            label={t('form:actions.resetPassword')}
            disabled={!isFormReadyToSubmit}
            isLoading={isPending}
            onClick={handleSubmit(handleSubmitForm)}
          />
        </BaseView>
      </BaseView>
    </>
  )
}

export default CreateNewPasswordScreen
